import { useFormik } from "formik";
import { AxiosError } from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import InputCustom from "../../../../../../components/InputCustom";
import Loading from "../../../../../../core/common/Loading";
import api from "../../../../../../services/api";
import { IOperacoesPatioGuiches } from "../../types/types";
import formValidator from "../validators/formValidator";
import { useAuth } from "../../../../../../hooks/AuthenticateContext";

interface FormValues {
  numero_guiche: string;
}

interface Props {
  isView?: boolean;
  isEdit?: boolean;
  selectedRow?: IOperacoesPatioGuiches;
  onConfirm: () => void;
  onClose: () => void;
}

type ApiErrorResponse = {
  error_msg?: string;
};

const getApiErrorMessage = (error: unknown) => {
  const axiosError = error as AxiosError<ApiErrorResponse>;
  return axiosError.response?.data?.error_msg || "Não foi possível cadastrar o guichê.";
};

const Form: React.FC<Props> = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useAuth();

  const handleSubmit = useCallback(
    async (values: FormValues, row?: IOperacoesPatioGuiches) => {
      try {
        setLoading(true);

        const userId = user?.id;

        const body = {
          id_operacao_patio_guiches: row?.id_operacao_patio_guiche,
          numero_guiche: values.numero_guiche,
          id_usuario_historico: userId,
          ativo: true,
        };

        if (props.isEdit) {
          await api.post("/editar/operacaoPatioGuiches", body);
        } else {
          await api.post("/cadastrar/operacaoPatioGuiches", body);
        }

        props.onConfirm();
      } catch (error: unknown) {
        toast.warning(getApiErrorMessage(error));
      } finally {
        setLoading(false);
      }
    },
    [props, user?.id]
  );

  const formik = useFormik<FormValues>({
    initialValues: {
      numero_guiche: "",
    },
    validationSchema: formValidator,
    onSubmit: (values) => {
      handleSubmit(values, props.selectedRow);
    },
  });

  const onLoadFormValues = useCallback(
    (row: IOperacoesPatioGuiches) => {
      if (row) {
        formik.setFieldValue("numero_guiche", row.numero_guiche);
      }
    },
    [formik]
  );

  useEffect(() => {
    if (props.selectedRow) {
      onLoadFormValues(props.selectedRow);
    }
  }, [props.selectedRow, onLoadFormValues]);

  return (
    <>
      <Loading loading={loading} skeletonFields={1} skeletonColumns={1} />
      <form onSubmit={formik.handleSubmit}>
        <div className="grid grid-cols-1 gap-4">
          <InputCustom
            title="Número do Guichê"
            name="numero_guiche"
            placeholder="Digite o número do guichê"
            onChange={(value) => formik.setFieldValue("numero_guiche", value)}
            value={formik.values.numero_guiche}
            error={formik.errors.numero_guiche}
            touched={formik.touched.numero_guiche}
            disabled={props.isView}
            type="number"
          />
        </div>
        <div className="flex justify-end mt-4">
          <button
            type="button"
            className="mr-2 px-4 py-2 bg-gray-300 rounded"
            onClick={props.onClose}
          >
            Cancelar
          </button>
          {!props.isView && (
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded"
            >
              Salvar
            </button>
          )}
        </div>
      </form>
    </>
  );
};

export default Form;
